import { resolveLdsHrMarginFromFeatures } from "../../../features/agentEntitlements/selectors";

/**
 * Search-card + profile package ILS for organized tours.
 * Agent-visible price is AFTER HR (`priceWithCommission`), never payment CC.
 * Do not treat a ~4.5% gap from initialPrice as CC — that is often HR markup.
 */

function toFiniteNumber(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickOurPrice(root, raw) {
  return (
    root?.ourPrice ||
    raw?.ourPrice ||
    raw?.fullDeal?.ourPrice ||
    null
  );
}

/** Detect checkout total ≈ package × (1 + ~4.5% CC). */
function looksLikePackagePlusCc(packageIls, withCc) {
  if (packageIls == null || withCc == null || packageIls <= 0) return false;
  const ratio = Number(withCc) / Number(packageIls);
  return ratio > 1.03 && ratio < 1.065;
}

/** Strip ~4.5% CC when fee field is missing but total looks baked-in. */
function stripImplicitCcFee(totalIls) {
  const t = toFiniteNumber(totalIls);
  if (t == null || t <= 0) return null;
  const stripped = Math.round(t / 1.045);
  if (stripped > 0 && looksLikePackagePlusCc(stripped, t)) return stripped;
  return null;
}

export function isOrganizedTourFixedPerPersonDeal(deal, our, otf) {
  if (String(otf?.hrMarginPricingMode || "").toLowerCase() === "fixed_per_person_embedded") {
    return true;
  }
  const ct = String(
    our?.ldsCommissionCondition?.commission_type ||
      deal?.ldsCommissionCondition?.commission_type ||
      deal?.ourPrice?.ldsCommissionCondition?.commission_type ||
      deal?.fullDeal?.ldsCommissionCondition?.commission_type ||
      ""
  )
    .trim()
    .toUpperCase();
  return ct === "FIXED_PER_PERSON";
}

function looksLikeInitialPlusHr(initial, pwc, hrAmt, hrPct) {
  if (initial == null || pwc == null) return false;
  if (hrAmt != null && Math.abs(initial + hrAmt - pwc) <= 1.5) return true;
  if (hrPct != null && hrPct > 0) {
    const expected = initial * (1 + hrPct / 100);
    if (expected > 0 && Math.abs(expected - pwc) / expected <= 0.015) return true;
  }
  return false;
}

/** PWC was copied from initialPrice (pre-HR) — common after room-matrix sync / BASIC reprice. */
function pwcLooksLikePreHrInitial(initial, pwc) {
  if (initial == null || pwc == null) return false;
  return Math.abs(Number(pwc) - Number(initial)) <= 2.5;
}

function pickHrAmountIls(otf, ourPrice) {
  const snap = otf?.pricingSnapshot || ourPrice?.pricingSnapshot;
  const hrTrace = snap?.hr_margin_resolver_trace;
  return (
    toFiniteNumber(otf?.hrCommission) ??
    toFiniteNumber(ourPrice?.hrCommission) ??
    toFiniteNumber(otf?.unifiedHrMargin) ??
    toFiniteNumber(snap?.hr_markup_line_item_ils) ??
    toFiniteNumber(hrTrace?.calculated_hr_margin_ils)
  );
}

function pickHrPercent(otf, ourPrice) {
  const snap = otf?.pricingSnapshot || ourPrice?.pricingSnapshot;
  const hrTrace = snap?.hr_margin_resolver_trace;
  return (
    toFiniteNumber(otf?.hrCommissionPercent) ??
    toFiniteNumber(otf?.unifiedHrMarginPercent) ??
    toFiniteNumber(ourPrice?.hrCommissionPercent) ??
    toFiniteNumber(snap?.hr_margin_percent) ??
    toFiniteNumber(hrTrace?.final_hr_margin_percent)
  );
}

/** Apply HR onto initial when PWC is missing or still equals pre-HR.
 * @returns {number|null} repaired/after-HR ILS, or null when caller should keep falling through
 */
function resolveAfterHrPackageIls(initial, pwc, hrAmt, hrPct) {
  if (looksLikeInitialPlusHr(initial, pwc, hrAmt, hrPct)) return pwc;
  // No initial → cannot detect/repair pre-HR stamp; let caller strip CC etc.
  if (initial == null) return null;
  const needApply = pwc == null || pwcLooksLikePreHrInitial(initial, pwc);
  if (!needApply) return pwc;
  if (hrAmt != null && hrAmt > 0) return Math.round(initial + hrAmt);
  if (hrPct != null && hrPct > 0) return Math.round(initial * (1 + hrPct / 100));
  return pwc;
}

/**
 * Agent organized-tour HR % from entitlements (BASIC 5 / PRO 10). Null when not loaded.
 * @param {object|null|undefined} entitlements
 */
export function readOrganizedTourAgentHrPercent(entitlements) {
  const features = entitlements?.features;
  if (!features || typeof features !== "object") return null;
  const { percent } = resolveLdsHrMarginFromFeatures(features, "organized_tour");
  const n = toFiniteNumber(percent);
  return n != null && n >= 0 ? n : null;
}

/**
 * Search rows often store supplier net in both initialPrice and priceWithCommission.
 * The profile later reprices with the agent's organized-tour HR and the number jumps.
 * Apply that percent at display time so the card and the first profile paint match.
 */
function alignOrganizedTourPackageToAgentHr(resolved, otf, agentHrPercent) {
  const mode = String(otf?.hrMarginPricingMode || "").toLowerCase();
  if (mode === "fixed_per_person_embedded") return resolved;
  const pct = toFiniteNumber(agentHrPercent);
  const initial = toFiniteNumber(otf?.initialPrice);
  if (pct == null || pct <= 0 || initial == null || initial <= 0) return resolved;
  const target = Math.round(Math.round(initial * (1 + pct / 100) * 100) / 100);
  if (resolved == null || pwcLooksLikePreHrInitial(initial, resolved)) return target;
  if (Math.abs(Number(resolved) - target) <= 15) return target;
  return resolved;
}

/**
 * Resolve package ILS from an ourTotalFare / ourPrice pair.
 * Default: after-HR `priceWithCommission`. Strip only an explicit CC fee.
 * FIXED embed: keep initialPrice when illicit % reapply raised PWC.
 * When PWC was stamped as pre-HR initial (profile sync), re-apply HR from otf/snapshot.
 */
function sanitizeOrganizedTourPackageIlsRaw(otf, ourPrice) {
  const pwc = toFiniteNumber(otf?.priceWithCommission ?? ourPrice?.priceWithCommission);
  const pwcc = toFiniteNumber(
    otf?.priceWithCommissionAndCreditCardFee ?? ourPrice?.priceWithCommissionAndCreditCardFee
  );
  const cc = toFiniteNumber(otf?.creditCardFee ?? ourPrice?.creditCardFee);
  const initial = toFiniteNumber(otf?.initialPrice);
  const hrAmt = pickHrAmountIls(otf, ourPrice);
  const hrPct = pickHrPercent(otf, ourPrice);
  const embedded =
    String(otf?.hrMarginPricingMode || "").toLowerCase() === "fixed_per_person_embedded";

  // FIXED embed SSOT: illicit % reapply raises PWC but leaves initialPrice.
  if (embedded && initial != null && (pwc == null || pwc > initial + 0.51)) {
    return initial;
  }

  if (pwc != null) {
    const pwcEqualsCheckout = pwcc != null && Math.abs(pwc - pwcc) < 0.51;
    const separateCheckout = toFiniteNumber(ourPrice?.priceWithCreditCardFee);
    // Payment CC is added on top of after-HR PWC (priceWithCreditCardFee = PWC + fee).
    // That fee must not be subtracted from the package the agent already saw.
    const ccAddedOnTop =
      cc != null &&
      cc > 0 &&
      separateCheckout != null &&
      Math.abs(separateCheckout - (pwc + cc)) <= 1.5;
    const hrExplainsGap = looksLikeInitialPlusHr(initial, pwc, hrAmt, hrPct);
    if (pwcEqualsCheckout && cc != null && cc > 0 && !ccAddedOnTop && !hrExplainsGap) {
      return pwc - cc;
    }
    const afterHr = resolveAfterHrPackageIls(initial, pwc, hrAmt, hrPct);
    if (afterHr != null) return afterHr;
    // Implicit 4.5% strip only when there is no initial/HR pair — never drop HR.
    if (pwcEqualsCheckout && (cc == null || cc <= 0) && initial == null && hrAmt == null && hrPct == null) {
      const stripped = stripImplicitCcFee(pwc);
      if (stripped != null) return stripped;
    }
    return pwc;
  }

  if (pwcc != null && cc != null && cc > 0) return pwcc - cc;

  if (pwcc != null && (cc == null || cc <= 0) && initial == null) {
    const stripped = stripImplicitCcFee(pwcc);
    if (stripped != null) return stripped;
  }

  const repairedFromInitial = resolveAfterHrPackageIls(initial, null, hrAmt, hrPct);
  if (repairedFromInitial != null) return repairedFromInitial;

  if (initial != null) return initial;

  return null;
}

export function sanitizeOrganizedTourPackageIls(otf, ourPrice, agentHrPercent) {
  return alignOrganizedTourPackageToAgentHr(
    sanitizeOrganizedTourPackageIlsRaw(otf, ourPrice),
    otf,
    agentHrPercent
  );
}

/**
 * @param {object} args
 * @param {object|null|undefined} args.root — dealRoot / fullDeal / raw
 * @param {object|null|undefined} [args.raw]
 * @param {object|null|undefined} [args.deal] — legacy card deal prop
 * @param {((deal: object) => object)|null|undefined} [args.calculateLDSDealFareForRoomsComposition]
 * @param {number|null|undefined} [args.agentHrPercent]
 * @returns {number|null}
 */
export function resolveOrganizedTourSearchCardPackageIls({
  root,
  raw,
  deal,
  calculateLDSDealFareForRoomsComposition,
  agentHrPercent,
} = {}) {
  const base = root || raw?.fullDeal || raw || deal || null;
  const our = pickOurPrice(base, raw || deal);
  const beOtf = our?.ourTotalFare && typeof our.ourTotalFare === "object" ? our.ourTotalFare : null;
  const isFixed = isOrganizedTourFixedPerPersonDeal(base || deal, our, beOtf);

  const fromFare = (otf, ourPrice) =>
    sanitizeOrganizedTourPackageIls(otf, ourPrice, agentHrPercent);

  // FIXED: live FE composition first — ignores polluted BE PWC (HR% / CC bleed).
  if (isFixed && typeof calculateLDSDealFareForRoomsComposition === "function") {
    try {
      const pricing = calculateLDSDealFareForRoomsComposition(base || deal);
      const feIls = fromFare(pricing?.ourTotalFare, pricing);
      if (feIls != null) return feIls;
    } catch {
      /* fall through */
    }
  }

  const beIls = fromFare(beOtf, our);
  if (beIls != null) return beIls;

  if (!isFixed && typeof calculateLDSDealFareForRoomsComposition === "function") {
    try {
      const pricing = calculateLDSDealFareForRoomsComposition(base || deal);
      const feIls = fromFare(pricing?.ourTotalFare, pricing);
      if (feIls != null) return feIls;
    } catch {
      /* fall through */
    }
  }

  const fare =
    base?.dealFare?.totalFare ??
    raw?.dealFare?.totalFare ??
    deal?.dealFare?.totalFare ??
    null;
  const n = toFiniteNumber(fare);
  return n;
}

/**
 * Live booking total from calculateLdsTotalPayment / room-matrix sync
 * (fields often sit on ourPrice root, not only ourTotalFare).
 */
function hasLiveOrganizedTourOurPriceTotal(ourPrice) {
  if (!ourPrice || typeof ourPrice !== "object") return false;
  const rootPwc = toFiniteNumber(ourPrice.priceWithCommission);
  if (rootPwc != null && rootPwc > 0) return true;
  const otf =
    ourPrice.ourTotalFare && typeof ourPrice.ourTotalFare === "object"
      ? ourPrice.ourTotalFare
      : null;
  const otfPwc = toFiniteNumber(otf?.priceWithCommission);
  return otfPwc != null && otfPwc > 0;
}

/**
 * Merge deal-level initial/HR onto live ourPrice so pre-HR PWC can be repaired
 * after org_tour_rooms_matrix_sync stamped priceWithCommission = initialPrice.
 */
function buildOrganizedTourSanitizePair(ourPrice, selectedResult) {
  const liveOtf =
    ourPrice?.ourTotalFare && typeof ourPrice.ourTotalFare === "object"
      ? ourPrice.ourTotalFare
      : null;
  const dealOur =
    selectedResult?.ourPrice ||
    selectedResult?.fullDeal?.ourPrice ||
    null;
  const dealOtf =
    dealOur?.ourTotalFare && typeof dealOur.ourTotalFare === "object"
      ? dealOur.ourTotalFare
      : null;
  const snap =
    liveOtf?.pricingSnapshot ||
    ourPrice?.pricingSnapshot ||
    dealOtf?.pricingSnapshot ||
    dealOur?.pricingSnapshot ||
    null;

  const mergedOtf = {
    ...(dealOtf || {}),
    ...(liveOtf || {}),
    pricingSnapshot: snap,
    priceWithCommission:
      toFiniteNumber(liveOtf?.priceWithCommission) ??
      toFiniteNumber(ourPrice?.priceWithCommission) ??
      toFiniteNumber(dealOtf?.priceWithCommission),
    priceWithCommissionAndCreditCardFee:
      toFiniteNumber(liveOtf?.priceWithCommissionAndCreditCardFee) ??
      toFiniteNumber(ourPrice?.priceWithCommissionAndCreditCardFee) ??
      toFiniteNumber(dealOtf?.priceWithCommissionAndCreditCardFee),
    creditCardFee:
      toFiniteNumber(liveOtf?.creditCardFee) ??
      toFiniteNumber(ourPrice?.creditCardFee) ??
      toFiniteNumber(dealOtf?.creditCardFee),
    initialPrice:
      toFiniteNumber(liveOtf?.initialPrice) ??
      toFiniteNumber(ourPrice?.initialPrice) ??
      toFiniteNumber(dealOtf?.initialPrice),
    hrCommission:
      toFiniteNumber(liveOtf?.hrCommission) ??
      toFiniteNumber(ourPrice?.hrCommission) ??
      toFiniteNumber(dealOtf?.hrCommission),
    hrCommissionPercent:
      toFiniteNumber(liveOtf?.hrCommissionPercent) ??
      toFiniteNumber(liveOtf?.unifiedHrMarginPercent) ??
      toFiniteNumber(ourPrice?.hrCommissionPercent) ??
      toFiniteNumber(dealOtf?.hrCommissionPercent) ??
      toFiniteNumber(dealOtf?.unifiedHrMarginPercent),
    hrMarginPricingMode:
      liveOtf?.hrMarginPricingMode || dealOtf?.hrMarginPricingMode,
  };

  const mergedOur = {
    ...(dealOur || {}),
    ...(ourPrice || {}),
    hrCommission: mergedOtf.hrCommission,
    hrCommissionPercent: mergedOtf.hrCommissionPercent,
    pricingSnapshot: snap,
  };

  return { otf: mergedOtf, our: mergedOur };
}

/**
 * Profile / booking sticky + footer display ILS — package + handling + trip guaranty, never payment CC.
 */
export function resolveOrganizedTourProfileDisplayIls({
  ourPrice,
  selectedResult,
  calculateLDSDealFareForRoomsComposition,
  agentHrPercent,
} = {}) {
  const handleFee = toFiniteNumber(ourPrice?.handleFee) ?? 0;
  const tripG = toFiniteNumber(ourPrice?.tripGuarantyPrice) ?? 0;
  const withCc = toFiniteNumber(ourPrice?.priceWithCreditCardFee);
  const cc = toFiniteNumber(ourPrice?.creditCardFee) ?? 0;

  const dealOur =
    selectedResult?.ourPrice || selectedResult?.fullDeal?.ourPrice || null;
  const isFixed = isOrganizedTourFixedPerPersonDeal(
    selectedResult,
    dealOur,
    dealOur?.ourTotalFare
  );

  // 1) FIXED: live FE composition first — ignores polluted BE PWC.
  if (isFixed) {
    const fromDeal = resolveOrganizedTourSearchCardPackageIls({
      root: selectedResult,
      raw: selectedResult,
      deal: selectedResult,
      calculateLDSDealFareForRoomsComposition,
      agentHrPercent,
    });
    if (fromDeal != null) {
      return fromDeal + handleFee + tripG;
    }
  }

  // Deal / search-card / TRACE «אחרי עמלת HR». A room-matrix reprice must not
  // replace this with a lower number (₪10,359 was showing as ₪10,024).
  const fromDeal = resolveOrganizedTourSearchCardPackageIls({
    root: selectedResult,
    raw: selectedResult,
    deal: selectedResult,
    calculateLDSDealFareForRoomsComposition,
    agentHrPercent,
  });

  // 2) Live context ourPrice wins only when it is at least the deal after-HR package
  // (added rooms / higher occupancy). A lower sync is the overwrite bug.
  // A live total that is only higher because the +₪1,000 fee is already inside
  // priceWithCommission must not be treated as a higher package and then get
  // the fee added again (₪10,358 + 1,000 was showing as ₪12,023.64).
  const { otf, our } = buildOrganizedTourSanitizePair(ourPrice, selectedResult);
  if (hasLiveOrganizedTourOurPriceTotal(ourPrice)) {
    let liveIls = sanitizeOrganizedTourPackageIls(otf, our, agentHrPercent);
    if (liveIls != null) {
      let packageIls = liveIls;
      if (fromDeal != null && handleFee > 0) {
        const withoutFee = liveIls - handleFee;
        const excess = liveIls - fromDeal;
        // +₪1,000 already sits on a base that is still under the after-HR package
        // (₪10,024 + 1,000 = ₪11,024). That rise is the fee, not an extra room.
        const feeExplainsTheRise =
          withoutFee + 0.51 < fromDeal &&
          excess > 0.51 &&
          excess + 0.51 >= handleFee * 0.5 &&
          excess <= handleFee + 1.5;
        packageIls =
          feeExplainsTheRise || liveIls + 0.51 < fromDeal ? fromDeal : liveIls;
      } else if (fromDeal != null && liveIls + 0.51 < fromDeal) {
        packageIls = fromDeal;
      }
      return packageIls + handleFee + tripG;
    }
  }

  // 3) Deal-level SSOT when live ourPrice is empty.
  if (fromDeal != null) {
    return fromDeal + handleFee + tripG;
  }

  let packageIls = sanitizeOrganizedTourPackageIls(otf, our, agentHrPercent);

  if (packageIls != null) {
    const packageKept =
      fromDeal != null && packageIls + 0.51 < fromDeal ? fromDeal : packageIls;
    return packageKept + handleFee + tripG;
  }

  // 4) Checkout total minus explicit CC.
  if (withCc != null && cc > 0) {
    return withCc - cc;
  }

  // 5) Fallback — prefer after-HR PWC; never strip HR as CC.
  if (withCc != null && otf) {
    const initial = toFiniteNumber(otf.initialPrice);
    const pwc = toFiniteNumber(otf.priceWithCommission);
    const hrAmt = pickHrAmountIls(otf, our);
    const hrPct = pickHrPercent(otf, our);
    const repaired = resolveAfterHrPackageIls(initial, pwc, hrAmt, hrPct);
    if (repaired != null) {
      return repaired + handleFee + tripG;
    }
    if (pwc != null) {
      return pwc + handleFee + tripG;
    }
    if (initial == null && hrAmt == null && hrPct == null) {
      const stripped = stripImplicitCcFee(withCc);
      if (stripped != null) return stripped + handleFee + tripG;
    }
  }

  if (withCc != null) {
    return withCc - cc;
  }

  return null;
}

/** Hide LDS template tokens like ###SUPPLIER_PREPARED### from profile UI. */
export function sanitizeOrganizedTourResourceDescription(raw) {
  if (raw == null) return "";
  let s = String(raw);
  s = s.replace(/###\s*SUPPLIER[_ ]?PREPARED\s*###/gi, "");
  s = s.replace(/#{2,}\s*[A-Z][A-Z0-9_]{2,}\s*#{2,}/g, "");
  return s.replace(/\s{2,}/g, " ").trim();
}
